import {
  Box,
  Button,
  Divider,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  Theme,
  Typography,
  alpha,
  useMediaQuery,
} from "@mui/material";
import { CustomInput } from "global/components";
import { CustomContactNumberInput } from "global/components/CustomContactNumberInput/CustomContactNumberInput";
import notifiers from "global/constants/NotificationConstants";
import strings from "global/constants/StringConstants";
import title from "global/constants/TitleConstants";
import urls from "global/constants/UrlConstants";
import viewpaths from "global/constants/ViewPathConstants";
import {
  convertResourceToObjectFormat,
  handleErrorMessage,
  hasGoogleAuthProvider,
  isTruthy,
  openErrorNotification,
  trackSignupComplete,
} from "helpers/methods";
import { RegisterUser } from "models/interfaces";
import { useState } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useLocation } from "react-router-dom";
import history from "utils/history";
import { useTitle } from "utils/UseTitle";
import {
  registerFormField,
  registrationValidation,
} from "./AuthTypesAndValidation";
import hidePasswordIcon from "assets/images/Hide.svg";
import showPasswordIcon from "assets/images/Show.svg";
import { createNewAccount, oAuthLogin } from "./AuthService";
import { loginAction, selectAuthenticated } from "redux/authSlice";
import { getAccountDisplayName } from "./authSession";
import { useAuthRouteRedirection } from "layouts/Config";
import { useAppSelector } from "redux/hooks";
import Clarity from "@microsoft/clarity";
import { GoogleLogin } from "@react-oauth/google";
import { useDispatch } from "react-redux";
import defaultImage from "assets/icons1/avatars/default.png";
import Logo from "assets/images/side_nav_inkfree_logo.svg";
import { focusRing, mobile, signer } from "theme/colors";
import {
  authRef,
  refFont,
  authSecondaryButtonWithIconSx,
  googleButtonSlot,
  googleOverlaySx,
  GOOGLE_BUTTON_WIDTH,
} from "./AuthReferenceStyles";
import { GoogleGlyph } from "./AuthGlyphs";

// "How it works" rows in the brand panel, per reference 2b.
const BRAND_FEATURES = [
  {
    title: "Upload anything",
    body: "PDF, DOCX, images — up to 10 MB, no formatting drama.",
    tint: alpha(signer.gradient.amber, 0.15),
    stroke: signer.gradient.amber,
    icon: (
      <>
        <path d="M12 3v12M7 10l5 5 5-5" />
        <path d="M4 19h16" />
      </>
    ),
  },
  {
    title: "Invite your signers",
    body: "Set the order, add reminders, and let Inkfree do the chasing.",
    tint: alpha(signer.gradient.coral, 0.16),
    stroke: signer.gradient.coral,
    icon: (
      <>
        <circle cx="9" cy="8" r="3.2" />
        <path d="M3.5 19c.9-3.2 3-4.8 5.5-4.8s4.6 1.6 5.5 4.8" />
        <path d="M16.5 8h5M19 5.5v5" />
      </>
    ),
  },
  {
    title: "Track every stroke",
    body: "Live status, audit trail, and a nudge button for slow signers.",
    tint: alpha(signer.purple.main, 0.25),
    // No exact token for this lighter purple; taken from the reference.
    stroke: "#B49BE0",
    icon: <path d="M4 12l5 5L20 6" />,
  },
];

const focusRingSx = {
  "&:focus-visible": {
    outline: `2px solid ${focusRing}`,
    outlineOffset: "2px",
  },
};

const Register = () => {
  useTitle(title.REGISTER);
  const isLoggedIn = useAppSelector(selectAuthenticated);
  const authRoute = useAuthRouteRedirection();
  const location = useLocation();
  // Prefill name/email when arriving from the signer "Sign up free" CTA.
  const prefill = (location.state as any) || {};
  const prefillNameParts = String(prefill.prefillName || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  const [formFields, setFormFields] = useState(() => {
    // registerFormField() returns a fresh object (email prefilled from the arg);
    // mutate the name fields so the state keeps its original full type.
    const base = registerFormField(prefill.prefillEmail || undefined);
    if (prefillNameParts[0]) {
      base.firstName.value = prefillNameParts[0];
    }
    if (prefillNameParts.length > 1) {
      base.lastName.value = prefillNameParts.slice(1).join(" ");
    }
    return base;
  });
  const [showPassword, setSetPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const planIdFromURL = new URLSearchParams(location.search).get("id");
  const { executeRecaptcha } = useGoogleReCaptcha();
  const mdUp = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));
  const dispatch = useDispatch();
  const handleOnchange = (event: React.ChangeEvent<any>) => {
    setFormFields({
      ...formFields,
      [event.target.name]: {
        ...formFields[event.target.name],
        value: event.target.value,
        error: "",
      },
    });
  };

  const createAccount = async (userData: RegisterUser) => {
    try {
      let token = "";
      if (urls.PROD) {
        token = await executeRecaptcha!();
      }
      setLoading(true);
      userData.captchaToken = token;
      await createNewAccount(userData);
      if (urls.PROD) {
        Clarity.setTag("account", userData.account);
        Clarity.setTag("email", userData.email);
        Clarity.identify(userData.email, userData.account, userData.account);
      }
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      if (error.message === strings.pendingVerificationErrorMessage) {
        history.push({
          pathname: viewpaths.verificationPendingViewPath,
          state: { email: userData.email, isFromRegistrationScreen: true },
        });
      } else {
        openErrorNotification(error.message);
      }
      throw error;
    }
  };

  const handleKeypress = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmitButton();
    }
  };

  const handleSubmitButton = async () => {
    try {
      if (handleValidation()) {
        const userData: RegisterUser = {
          pwd: formFields.password?.value,
          authToken: "",
          name: formFields.firstName?.value + " " + formFields.lastName?.value,
          contactNo: formFields.contact?.value,
          email: formFields.email?.value,
          role: "",
          resources: [],
          account: formFields.company?.value,
          company: formFields.company?.value,
          captchaToken: "",
          planId: planIdFromURL ?? strings.FREE_PLAN_ID,
        };
        await createAccount(userData);
        // Signup succeeded — fire the GTM event tagged with the chosen plan.
        trackSignupComplete(userData.planId);
        history.push({
          pathname: viewpaths.registrationSuccessViewPath,
          state: { email: formFields.email?.value },
        });
      }
    } catch (error: any) {
      if (error.message !== strings.pendingVerificationErrorMessage) {
        openErrorNotification(
          isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR,
        );
      }
    }
  };

  const logInPage = () => {
    history.push(viewpaths.loginViewPath);
  };

  const handleValidation = () => {
    const { errors, isValid } = registrationValidation(formFields);
    setFormFields({ ...errors });
    return isValid;
  };

  const handleOauthLogin = async (token: string, provider: string) => {
    try {
      setLoading(true);
      const res = await oAuthLogin(token, provider);
      setLoading(false);
      return res;
    } catch (error: any) {
      setLoading(false);
      handleErrorMessage(error);
    }
  };

  const handleGoogleLoginSuccess = async (credentialResponse: any) => {
    const token = credentialResponse.credential;
    let user: any;
    if (!token) return;
    try {
      user = await handleOauthLogin(token, strings.OAuthProviders.googleAuth);

      if (user?.authToken) {
        const formattedResources = convertResourceToObjectFormat(
          user.resources || {},
        );

        dispatch(
          loginAction({
            authenticated: true,
            accessToken: user.authToken,
            refreshToken: user.refreshToken,
            userName: user.name,
            email: (user.email ?? "").toLowerCase(),
            userRole: user.role,
            resources: formattedResources,
            profileImage: user.profilePicture
              ? "data:image/png;base64," + user.profilePicture
              : defaultImage,
            deviceId: "",
            account: user.account,
            accountDisplayName: getAccountDisplayName(user),
            userId: user.id,
            isTrial: user.trial,
            lastLogin: user.lastLogin,
          }),
        );
        history.push(viewpaths.dashboardViewPath);
      } else {
        const hasGoogleProvider = hasGoogleAuthProvider(
          user.authProviders || [],
        );

        if (hasGoogleProvider) {
          const googleUserData = {
            email: user.email,
            name: user.name || "",
            authProviders: user.authProviders || [],
          };

          history.push({
            pathname: viewpaths.oAuthRegisterViewPath,
            state: googleUserData,
          });
        }
      }
    } catch (error: any) {
      if (error.message === strings.pendingVerificationErrorMessage) {
        history.push({
          pathname: viewpaths.verificationPendingViewPath,
          state: {
            email: user?.email,
            isFromLoginScreen: true,
          },
        });
      } else {
        handleErrorMessage(error);
      }
    }
  };

  if (isLoggedIn) {
    history.push(authRoute);
    return null;
  }

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        width: "100%",
        overflowX: "hidden",
        backgroundColor: "background.paper",
      }}
    >
      <Box
        component="main"
        id="main-content"
        tabIndex={-1}
        sx={{
          flex: { xs: "1 1 100%", md: "0 0 50%" },
          minWidth: 0,
          maxWidth: { xs: "100%", md: "50%" },
          display: "flex",
          flexDirection: "column",
          backgroundColor: "background.paper",
          boxSizing: "border-box",
          outline: "none",
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            px: { xs: 3, sm: 5, md: 7, lg: 9 },
            pt: { xs: 4, sm: 5, md: 5 },
            pb: { xs: 4, sm: 3.5 },
            maxWidth: { xs: "100%", sm: 600, md: 640 },
            width: "100%",
            mx: "auto",
            boxSizing: "border-box",
          }}
        >
          <Box sx={{ mb: { xs: 4, md: 5 } }}>
            <img
              src={Logo}
              alt="Inkfree logo"
              style={{ width: 112, height: "auto", display: "block" }}
            />
          </Box>

          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              mt: { xs: 2, md: 0 },
            }}
          >
            <Typography
              component="h1"
              sx={{
                fontSize: { xs: "1.625rem", sm: refFont.headingCompact },
                fontWeight: 800,
                color: mobile.base[500],
                mb: 1,
                lineHeight: 1.2,
                letterSpacing: "-0.01em",
              }}
            >
              Create Account
            </Typography>

            <Typography
              sx={{
                mb: 3,
                fontSize: refFont.body,
                color: authRef.subText,
              }}
            >
              Already have an account?{" "}
              <Link
                component="span"
                onClick={logInPage}
                role="button"
                tabIndex={0}
                onKeyPress={(e: React.KeyboardEvent) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    logInPage();
                  }
                }}
                sx={{
                  cursor: "pointer",
                  color: signer.orange.main,
                  fontSize: "inherit",
                  fontWeight: 700,
                  textDecoration: "none",
                  "&:hover": { textDecoration: "underline" },
                  ...focusRingSx,
                }}
                aria-label="Login to your account"
              >
                Login
              </Link>
            </Typography>

            <Box sx={{ ...googleButtonSlot, width: "100%" }}>
              <Button
                fullWidth
                variant="outlined"
                disabled={loading}
                tabIndex={-1}
                aria-hidden
                startIcon={<GoogleGlyph />}
                sx={authSecondaryButtonWithIconSx}
              >
                Sign up with Google
              </Button>
              <Box sx={googleOverlaySx(loading)}>
                <GoogleLogin
                  theme="outline"
                  size="large"
                  width={GOOGLE_BUTTON_WIDTH}
                  text="signup_with"
                  onSuccess={handleGoogleLoginSuccess}
                />
              </Box>
            </Box>

            <Divider
              sx={{
                my: 2.75,
                "&:before, &:after": { borderColor: authRef.dividerLine },
              }}
            >
              <Typography
                component="span"
                sx={{
                  color: authRef.metaText,
                  px: 1.5,
                  fontWeight: 600,
                  fontSize: refFont.divider,
                  letterSpacing: "0.08em",
                }}
              >
                OR WITH EMAIL
              </Typography>
            </Divider>

            <Box
              sx={{
                // Field chrome per reference 2b: 44px tall, 8px radius, pale
                // fill, 14px inset. `&&` outranks CustomInput's !important.
                "&& .MuiOutlinedInput-root": {
                  minHeight: "44px",
                  height: "44px",
                  borderRadius: "8px",
                  padding: "0 14px !important",
                  backgroundColor: authRef.inputBg,
                  fontSize: refFont.body,
                  fontWeight: 500,
                  color: mobile.base[500],
                  "& fieldset": { borderColor: authRef.inputBorder },
                  "&:hover fieldset": { borderColor: authRef.inputBorderHover },
                },
                "&& .MuiInputBase-input": { padding: "0 !important" },
                "&& .MuiInputBase-input::placeholder": {
                  color: authRef.metaText,
                  opacity: 1,
                },
                "&& .MuiInputLabel-root": {
                  fontSize: refFont.label,
                  fontWeight: 600,
                  color: mobile.base[500],
                  marginLeft: 0,
                  "& .MuiFormLabel-asterisk": { color: signer.orange.main },
                },
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <CustomInput
                    label="First Name"
                    required
                    placeHolder="Enter your first name"
                    type="text"
                    name="firstName"
                    id="firstName"
                    value={formFields.firstName.value}
                    propsToInputElement={{
                      maxlength: 20,
                      "aria-required": true,
                      "aria-invalid": !!formFields.firstName.error,
                    }}
                    onChange={handleOnchange}
                    onKeyPress={handleKeypress}
                    error={
                      !isTruthy(formFields.firstName.value) &&
                      formFields.firstName.error
                    }
                    helperText={
                      !isTruthy(formFields.firstName.value)
                        ? formFields.firstName.error
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CustomInput
                    label="Last Name"
                    required
                    id="lastName"
                    placeHolder="Enter your last name"
                    type="text"
                    name="lastName"
                    value={formFields.lastName.value}
                    propsToInputElement={{
                      maxlength: 20,
                      "aria-required": true,
                      "aria-invalid": !!formFields.lastName.error,
                    }}
                    onChange={handleOnchange}
                    onKeyPress={handleKeypress}
                    error={
                      !isTruthy(formFields.lastName.value) &&
                      formFields.lastName.error
                    }
                    helperText={
                      !isTruthy(formFields.lastName.value)
                        ? formFields.lastName.error
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CustomInput
                    label="Company Name"
                    required
                    id="companyName"
                    placeHolder="Enter your company name"
                    type="text"
                    name="company"
                    value={formFields.company.value}
                    propsToInputElement={{
                      maxlength: 25,
                      "aria-required": true,
                      "aria-invalid": !!formFields.company.error,
                    }}
                    onChange={handleOnchange}
                    onKeyPress={handleKeypress}
                    error={
                      !isTruthy(formFields.company.value) &&
                      formFields.company.error
                    }
                    helperText={
                      !isTruthy(formFields.company.value)
                        ? formFields.company.error
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CustomContactNumberInput
                    label="Contact Number"
                    required
                    id="contactNumber"
                    value={formFields.contact?.value}
                    placeHolder="Enter contact number"
                    onChange={(phone: any) => {
                      setFormFields({
                        ...formFields,
                        contact: {
                          value: phone,
                          error: "",
                        },
                      });
                    }}
                    error={
                      isTruthy(formFields.contact.error) &&
                      formFields.contact.error
                    }
                    inputProps={{
                      "aria-required": true,
                      "aria-invalid": !!formFields.contact.error,
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <CustomInput
                    label="Email"
                    required
                    id="email"
                    placeHolder="Enter your email"
                    type="email"
                    name="email"
                    value={formFields.email.value}
                    propsToInputElement={{
                      maxlength: 45,
                      "aria-required": true,
                      "aria-invalid": !!formFields.email.error,
                    }}
                    onChange={handleOnchange}
                    onKeyPress={handleKeypress}
                    error={
                      !isTruthy(formFields.email.value) &&
                      formFields.email.error
                    }
                    helperText={
                      !isTruthy(formFields.email.value)
                        ? formFields.email.error
                        : ""
                    }
                  />
                  {!strings.regex.test(formFields.email.value) &&
                    formFields.email.value.length > 0 && (
                      <FormHelperText error sx={{ ml: 2 }} role="alert">
                        Please enter valid email id
                      </FormHelperText>
                    )}
                </Grid>
                <Grid item xs={12}>
                  <CustomInput
                    label="Password"
                    required
                    placeHolder="••••••••"
                    id="password"
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formFields.password.value}
                    onChange={handleOnchange}
                    onKeyPress={handleKeypress}
                    error={
                      !isTruthy(formFields.password.value.trim()) &&
                      formFields.password.error
                    }
                    helperText={
                      !isTruthy(formFields.password.value.trim())
                        ? formFields.password.error
                        : ""
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="start">
                          <IconButton
                            aria-label={
                              showPassword ? "Hide password" : "Show password"
                            }
                            onClick={() => setSetPassword(!showPassword)}
                            edge="end"
                            tabIndex={0}
                            onKeyPress={(e) => {
                              if (e.key === "Enter" || e.key === " ")
                                setSetPassword(!showPassword);
                            }}
                          >
                            <Box
                              component="img"
                              src={
                                showPassword
                                  ? showPasswordIcon
                                  : hidePasswordIcon
                              }
                              alt={
                                showPassword ? "Hide password" : "Show password"
                              }
                              aria-hidden="true"
                            />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    propsToInputElement={{
                      "aria-required": true,
                      "aria-invalid": !!formFields.password.error,
                    }}
                  />
                  {formFields.password.value &&
                    !strings.passwordValidationRegex.test(
                      formFields.password.value.trim(),
                    ) && (
                      <FormHelperText error sx={{ ml: 2 }} role="alert">
                        Please enter a password that is at least 8 characters
                        long and includes at least one digit, one lowercase
                        letter, one uppercase letter, and one special character
                        from the following: @ $ ! % * ? & #.
                      </FormHelperText>
                    )}
                </Grid>
              </Grid>
            </Box>

            <Button
              fullWidth
              size="large"
              sx={{
                mt: 3,
                height: "50px",
                borderRadius: "8px",
                fontWeight: 700,
                fontSize: refFont.button,
                letterSpacing: 0,
              }}
              type="submit"
              variant="contained"
              onClick={handleSubmitButton}
              disabled={loading}
              aria-label="Create Account"
            >
              {loading ? "Creating account..." : "Create Account"}
            </Button>

            <Typography
              sx={{
                display: "block",
                mt: 2,
                color: authRef.metaText,
                fontSize: refFont.legal,
                lineHeight: 1.6,
              }}
            >
              By creating an account, you agree to our{" "}
              <Box
                component="a"
                href="https://inkfree.app/terms/"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: authRef.subText,
                  textDecoration: "underline",
                  "&:hover": { color: mobile.base[500] },
                }}
              >
                Terms of Service
              </Box>{" "}
              and{" "}
              <Box
                component="a"
                href="https://inkfree.app/privacy-policy/"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: authRef.subText,
                  textDecoration: "underline",
                  "&:hover": { color: mobile.base[500] },
                }}
              >
                Privacy Policy
              </Box>
              .
            </Typography>
          </Box>

          <Box component="footer" role="contentinfo" sx={{ mt: { xs: 4, md: 5 } }}>
            <Typography
              sx={{
                color: authRef.metaText,
                display: "block",
                fontSize: refFont.footer,
                lineHeight: 1.6,
              }}
            >
              Protected by reCAPTCHA · © {new Date().getFullYear()} Inkfree. All
              rights reserved.
            </Typography>
          </Box>
        </Box>
      </Box>

      {mdUp && (
        <Box
          id="register-right-panel"
          component="aside"
          aria-label="What you can do with Inkfree"
          sx={{
            flex: "0 0 50%",
            maxWidth: "50%",
            position: "relative",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            px: { md: 6, lg: 8 },
            backgroundColor: mobile.base[500],
          }}
        >
          {/* Ambient brand glow */}
          <Box
            aria-hidden="true"
            sx={{
              position: "absolute",
              inset: 0,
              background: `radial-gradient(120% 90% at 85% 10%, ${alpha(
                signer.purple.main,
                0.55,
              )}, transparent 60%), radial-gradient(100% 80% at 20% 100%, ${alpha(
                signer.gradient.coral,
                0.28,
              )}, transparent 55%)`,
            }}
          />

          <Box
            sx={{
              position: "relative",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              textAlign: "left",
              maxWidth: 560,
              width: "100%",
              mr: "auto",
            }}
          >
            <Typography
              component="h2"
              sx={{
                // Matches the Login brand panel so the two screens read as one.
                fontSize: { md: "3rem", lg: "3.25rem" },
                fontWeight: 800,
                color: "common.white",
                lineHeight: 1.25,
                letterSpacing: "-0.01em",
              }}
            >
              Your first signature is{" "}
              <Box
                component="span"
                sx={{
                  background: `linear-gradient(90deg, ${signer.gradient.amber}, ${signer.gradient.coral})`,
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  color: "transparent",
                }}
              >
                minutes away.
              </Box>
            </Typography>

            {/* How it works */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2.75,
                mt: 5,
                width: "100%",
              }}
            >
              {BRAND_FEATURES.map((feature) => (
                <Box
                  key={feature.title}
                  sx={{ display: "flex", gap: 1.75, alignItems: "flex-start" }}
                >
                  <Box
                    aria-hidden="true"
                    sx={{
                      width: 38,
                      height: 38,
                      flexShrink: 0,
                      borderRadius: "9px",
                      backgroundColor: feature.tint,
                      color: feature.stroke,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <svg
                      width="19"
                      height="19"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      {feature.icon}
                    </svg>
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "1rem",
                        fontWeight: 700,
                        color: "common.white",
                      }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.875rem",
                        lineHeight: 1.55,
                        mt: 0.25,
                        color: alpha("#FFFFFF", 0.65),
                      }}
                    >
                      {feature.body}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>

            {/* Testimonial */}
            <Box
              sx={{
                mt: 5.5,
                width: "100%",
                maxWidth: 480,
                boxSizing: "border-box",
                display: "flex",
                alignItems: "center",
                gap: 1.75,
                p: "18px 20px",
                borderRadius: "12px",
                backgroundColor: alpha("#FFFFFF", 0.06),
                border: `1px solid ${alpha("#FFFFFF", 0.12)}`,
              }}
            >
              <Box
                aria-hidden="true"
                sx={{
                  width: 42,
                  height: 42,
                  flexShrink: 0,
                  borderRadius: "50%",
                  background: `linear-gradient(135deg, ${signer.purple.main}, ${signer.gradient.coral})`,
                  color: "common.white",
                  fontSize: "0.9375rem",
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                RM
              </Box>
              <Typography
                sx={{
                  fontSize: "0.875rem",
                  lineHeight: 1.6,
                  fontStyle: "italic",
                  color: alpha("#FFFFFF", 0.8),
                }}
              >
                "We closed a 3-party agreement before lunch. Our old process
                took a week."
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};
export default Register;
